import { Bell, CircleCheck, Share2, Zap } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "~/components/ui/sonner";
import { useNotificationPolling } from "~/notifications/data-source/use-notification-polling";
import {
  getNotificationToastDescription,
  getNotificationToastTitle,
  getNotificationUrl,
  type Notification,
} from "~/notifications/model/notification";

// 30-second lookback window: suppresses historical events from before this session
// while still catching notifications that arrived just before the page loaded
// (e.g. share-then-reload) and absorbing normal server/client clock skew.
const SESSION_START = new Date(Date.now() - 30_000);

const INDIVIDUAL_THRESHOLD = 3;
const EXPAND_CHUNK = 3;
const INBOX_ROUTE = "/notifications";
const INDIVIDUAL_TOAST_DURATION_MS = 10_000;
const SUMMARY_TOAST_DURATION_MS = 15_000;

export function useNotificationToasts() {
  const pollingQuery = useNotificationPolling({ enabled: true });
  const navigate = useNavigate();
  const pollingData = pollingQuery.data;

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const previousIsErrorRef = useRef(false);
  useEffect(() => {
    if (pollingQuery.isError && !previousIsErrorRef.current) {
      console.error("[notifications] polling failed", pollingQuery.error);
    }
    previousIsErrorRef.current = pollingQuery.isError;
  }, [pollingQuery.isError, pollingQuery.error]);

  useEffect(() => {
    if (!pollingData) return;

    const fresh = pollingData.filter((n) => n.createdAt >= SESSION_START);
    if (fresh.length === 0) return;

    if (fresh.length <= INDIVIDUAL_THRESHOLD) {
      for (const notification of fresh) fireIndividual(notification);
      return;
    }

    fireSummary(fresh);

    function fireIndividual(notification: Notification) {
      toast(getNotificationToastTitle(notification), {
        id: `notif-${notification.id}`,
        description: getNotificationToastDescription(notification),
        icon: getNotificationIcon(notification),
        duration: INDIVIDUAL_TOAST_DURATION_MS,
        action: {
          label: "View",
          onClick: () => navigateRef.current(getNotificationUrl(notification)),
        },
      });
    }

    function fireSummary(batch: Notification[]) {
      const id = `notif-summary-${batch[0].id}`;
      toast(`${batch.length} new notification${batch.length === 1 ? "" : "s"}`, {
        id,
        icon: <Bell className="size-4" aria-hidden />,
        duration: SUMMARY_TOAST_DURATION_MS,
        action: {
          label: "View inbox",
          onClick: () => navigateRef.current(INBOX_ROUTE),
        },
        cancel: {
          label: "Expand",
          onClick: () => {
            toast.dismiss(id);
            expandBatch(batch);
          },
        },
      });
    }

    function expandBatch(batch: Notification[]) {
      const chunk = batch.slice(0, EXPAND_CHUNK);
      const remaining = batch.slice(EXPAND_CHUNK);

      for (const notification of chunk) fireIndividual(notification);

      if (remaining.length > INDIVIDUAL_THRESHOLD) {
        fireSummary(remaining);
      } else {
        for (const notification of remaining) fireIndividual(notification);
      }
    }
  }, [pollingData]);
}

function getNotificationIcon(notification: Notification) {
  if (notification.type === "share") {
    return <Share2 className="size-4 text-blue-500" aria-hidden />;
  }
  if (notification.payload.jobStatus === "completed") {
    return <CircleCheck className="size-4 text-green-500" aria-hidden />;
  }
  return <Zap className="size-4 text-red-500" aria-hidden />;
}
